/**
 * §5 — Role + per-token instructions picker.
 *
 * Floating panel surfaced when the coach taps a player token in the
 * board's edit mode. Lets them swap the player's role inside the
 * formation slot (e.g. CB → stopper / ball-playing / wide-CB / sweeper)
 * and toggle the five compact instruction enums defined in
 * `@4cm/core/tactics/roles`.
 *
 * Standalone for now — the boards mount it next round. Keeps no
 * internal state: the parent passes `current` and receives `next` on
 * every change so the layout store is the single source of truth.
 */
import { useEffect, useMemo, useRef } from 'react';
import {
  ROLE_VOCABULARY,
  INSTRUCTION_FIELDS,
  defaultRoleSelectionForSlot,
  type PlayerRoleSelection,
} from '@4cm/core/tactics/roles';
import { HudChip } from './HudChip';
import './RoleInstructionsPicker.css';

export interface RoleInstructionsPickerProps {
  /** `team-index` key of the token being edited — e.g. 'home-3'. Used
   *  for the panel header and aria-labelling only; the parent owns the
   *  mapping from key → selection. */
  tokenKey: string;
  /** Formation slot code the token sits in (GK / CB / CM / …). Drives
   *  the role select dropdown and the header role/slot label. */
  slot: string;
  /** Current persisted selection. When omitted, the default selection
   *  for the slot is rendered so the panel never has empty fields. */
  current?: PlayerRoleSelection;
  /** Fired on every chip / select change. Parent merges into store. */
  onChange: (next: PlayerRoleSelection) => void;
  /** Close (X) button + Escape key both fire this. */
  onClose: () => void;
}

// Display strings — role identifiers are kebab-case; humanise for the
// dropdown without losing the lossless storage identifier.
function humaniseRole(role: string): string {
  return role
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function humaniseValue(value: string): string {
  // 'free-roam' → 'Free Roam'. Keep enums in storage lowercase-kebab.
  return value
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

function teamLabel(tokenKey: string): string {
  const team = tokenKey.split('-')[0];
  if (team === 'home') return 'HOME';
  if (team === 'away') return 'AWAY';
  return team?.toUpperCase() ?? '—';
}

function tokenIndex(tokenKey: string): string {
  return tokenKey.split('-')[1] ?? '';
}

export function RoleInstructionsPicker({
  tokenKey,
  slot,
  current,
  onChange,
  onClose,
}: RoleInstructionsPickerProps) {
  // Resolve the working selection. Defaulting here keeps the parent's
  // job tiny — it only needs to commit `next` when the coach actually
  // touches something, instead of pre-seeding stores at mount time.
  const selection = useMemo<PlayerRoleSelection>(
    () => current ?? defaultRoleSelectionForSlot(slot),
    [current, slot],
  );

  const roleOptions = ROLE_VOCABULARY[slot] ?? [selection.role];
  const selectRef = useRef<HTMLSelectElement | null>(null);

  // Loose focus trap: park focus on the role select once on mount so
  // keyboard users can immediately tab forward into the instruction
  // rows. Not a hard trap — coaches reach for the close button via
  // Escape (handled below) rather than Tab cycling.
  useEffect(() => {
    selectRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  function emit(partial: Partial<PlayerRoleSelection>) {
    onChange({ ...selection, ...partial });
  }

  function setInstruction(key: string, value: string) {
    onChange({
      ...selection,
      instructions: { ...selection.instructions, [key]: value },
    });
  }

  return (
    <div
      className="role-picker"
      role="dialog"
      aria-modal="true"
      aria-label={`Edit role and instructions for ${teamLabel(tokenKey)} player ${tokenIndex(tokenKey)}`}
    >
      <div className="role-picker__header">
        <h2 className="role-picker__title">
          <span className="role-picker__team" data-team={teamLabel(tokenKey).toLowerCase()}>
            {teamLabel(tokenKey)}
          </span>
          <span className="role-picker__sep">·</span>
          <span className="role-picker__slot">{tokenIndex(tokenKey)} {slot}</span>
          {/* Role label intentionally not duplicated here — the role
              select below renders the same humanised string in its
              selected `<option>`, so the test's
              `screen.getByText('Ball Playing')` should land on exactly
              one element. */}
        </h2>
        <button
          type="button"
          className="role-picker__close"
          onClick={onClose}
          aria-label="Close role and instructions picker"
        >
          ×
        </button>
      </div>

      <div className="role-picker__row" data-label="role">
        <label className="role-picker__label" htmlFor={`role-${tokenKey}`}>
          Role
        </label>
        <select
          ref={selectRef}
          id={`role-${tokenKey}`}
          className="role-picker__select"
          value={selection.role}
          onChange={(e) => emit({ role: e.target.value })}
        >
          {roleOptions.map((opt) => (
            <option key={opt} value={opt}>
              {humaniseRole(opt)}
            </option>
          ))}
        </select>
      </div>

      {INSTRUCTION_FIELDS.map((field) => {
        const currentValue =
          selection.instructions[field.key] ?? field.default;
        return (
          <div
            key={field.key}
            className="role-picker__row"
            data-label={field.key}
          >
            <span className="role-picker__label">{field.label}</span>
            <div className="role-picker__chips">
              {field.values.map((value) => (
                <HudChip
                  key={value}
                  active={currentValue === value}
                  ariaLabel={`${field.label}: ${humaniseValue(value)}`}
                  onClick={() => setInstruction(field.key, value)}
                >
                  {humaniseValue(value)}
                </HudChip>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
